import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

export type DynamicTableColumnType = 'text' | 'number' | 'currency' | 'percentage' | 'badge';

export interface DynamicTableBadgeStyle {
  background?: string;
  color?: string;
  borderColor?: string;
}

export interface DynamicTableHoverPopupField {
  label: string;
  field?: string | string[];
  fields?: string[];
  calculation?: 'discountedPrice';
  baseField?: string | string[];
  discountField?: string | string[];
  type?: DynamicTableColumnType;
  format?: string;
  decimals?: number;
  currency?: string;
  compact?: boolean;
  valueMultiplier?: number;
  prefix?: string;
  suffix?: string;
  emptyValue?: string;
}

export interface DynamicTableHoverPopupConfig {
  enabled?: boolean;
  fields?: DynamicTableHoverPopupField[];
  api?: {
    enabled?: boolean;
    apiUrl?: string;
    method?: string;
    functionName?: string;
    version?: string;
    entity?: string;
    workflowId?: string;
    dataPath?: string;
    responsePath?: string;
    requestFieldMap?: Record<string, string | string[]>;
    requestData?: Record<string, any>;
    requestValuesAsArray?: boolean;
    title?: string;
    subtitle?: string;
    loadingMessage?: string;
    errorMessage?: string;
  };
}

export interface DynamicTableColumnConfig {
  field: string;
  header: string;
  type?: DynamicTableColumnType;
  bold?: boolean;
  align?: 'left' | 'center' | 'right';
  width?: string | number;
  minWidth?: string | number;
  decimals?: number;
  currency?: string;
  compact?: boolean;
  valueMultiplier?: number;
  prefix?: string;
  suffix?: string;
  emptyValue?: string;
  badgeConfig?: Record<string, DynamicTableBadgeStyle>;
  hoverPopup?: DynamicTableHoverPopupConfig;
}

export interface DynamicTableConfig {
  dataPath?: string;
  columns: DynamicTableColumnConfig[];
  sort?: {
    field?: string;
    fields?: string[];
    direction?: 'asc' | 'desc' | string;
    type?: 'natural' | 'number' | string;
    byFirstColumn?: boolean;
  };
  height?: string | number;
  maxHeight?: string | number;
  minHeight?: string | number;
  viewportOffset?: string | number;
  scrollMaxHeight?: string | number;
  scrollViewportOffset?: string | number;
  mobileViewportOffset?: string | number;
  mobileScrollViewportOffset?: string | number;
  stickyHeader?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  exportFileName?: string;
  defaultHeaderAlign?: 'left' | 'center' | 'right';
  defaultCellAlign?: 'left' | 'center' | 'right';
  headerCellPadding?: string;
  bodyCellPadding?: string;
  headerPadding?: string;
  scrollPadding?: string;
  tableMinWidth?: string;
  titleFontSize?: string | number;
  headerFontSize?: string | number;
  bodyFontSize?: string | number;
  headerTextColor?: string;
  headerBackgroundColor?: string;
  rowBackgroundColor?: string;
  altRowBackgroundColor?: string;
  autoHeight?: boolean;
  scrollAfterRows?: number;
}

@Component({
  selector: 'app-dynamic-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dynamic-table.component.html',
  styleUrl: './dynamic-table.component.scss',
})
export class DynamicTableComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  @ViewChild('cardRoot') private cardRoot?: ElementRef<HTMLElement>;
  @ViewChild('tableHeader') private tableHeader?: ElementRef<HTMLElement>;
  @ViewChild('tableScroll') private tableScroll?: ElementRef<HTMLElement>;

  loading = true;
  errorMessage = '';
  rows: any[] = [];
  activeHoverPopup: {
    columns: { label: string }[];
    rows: { values: string[] }[];
    title: string;
    subtitle: string;
    loading?: boolean;
    errorMessage?: string;
  } | null = null;

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private reflowSub?: Subscription;
  private popupRequestSub?: Subscription;
  private heightRafId: number | null = null;
  private popupRequestId = 0;

  get tableConfig(): DynamicTableConfig {
    return this.data?.tableConfig || {};
  }

  get columns(): DynamicTableColumnConfig[] {
    return Array.isArray(this.tableConfig.columns) ? this.tableConfig.columns : [];
  }

  get title(): string {
    return String(this.data?.title || this.tableConfig?.['title' as keyof DynamicTableConfig] || '');
  }

  get exportEnabled(): boolean {
    return this.data?.exportEnabled === true || this.data?.tableConfig?.exportEnabled === true;
  }

  get stickyHeader(): boolean {
    return this.tableConfig.stickyHeader !== false;
  }

  get emptyMessage(): string {
    return String(this.tableConfig.emptyMessage || this.data?.emptyMessage || 'No data available.');
  }

  get loadingMessage(): string {
    return String(this.tableConfig.loadingMessage || this.data?.loadingMessage || 'Loading...');
  }

  get tableMaxHeight(): string {
    if (this.shouldUseAutoHeight) return '';
    if (this.data?.tableHeight) return String(this.data.tableHeight);
    return this.toCssSize(this.tableConfig.maxHeight ?? this.data?.maxTableHeight);
  }

  get cardHeight(): string | null {
    if (this.shouldUseAutoHeight) return null;
    if (this.data?.height) return String(this.data.height);
    return this.toCssSize(this.tableConfig.height) || null;
  }

  get tableStyle(): Record<string, string> {
    const style: Record<string, string> = {};
    const headerCellPadding = String(this.tableConfig.headerCellPadding || '').trim();
    const bodyCellPadding = String(this.tableConfig.bodyCellPadding || '').trim();
    const headerPadding = String(this.tableConfig.headerPadding || '').trim();
    const scrollPadding = String(this.tableConfig.scrollPadding || '').trim();
    const tableMinWidth = String(this.tableConfig.tableMinWidth || '').trim();
    const titleFontSize = this.toCssSize(this.tableConfig.titleFontSize ?? this.data?.titleFontSize);
    const headerFontSize = this.toCssSize(this.tableConfig.headerFontSize ?? this.data?.headerFontSize);
    const bodyFontSize = this.toCssSize(this.tableConfig.bodyFontSize ?? this.data?.bodyFontSize);
    const headerTextColor = String(this.tableConfig.headerTextColor || this.data?.headerTextColor || '').trim();
    const headerBackgroundColor = String(
      this.tableConfig.headerBackgroundColor || this.data?.headerBackgroundColor || '',
    ).trim();
    const rowBackgroundColor = String(this.tableConfig.rowBackgroundColor || this.data?.rowBackgroundColor || '').trim();
    const altRowBackgroundColor = String(
      this.tableConfig.altRowBackgroundColor || this.data?.altRowBackgroundColor || '',
    ).trim();
    const minHeight = this.toCssSize(this.tableConfig.minHeight ?? this.data?.minHeight);
    const viewportOffset = this.toCssSize(this.tableConfig.viewportOffset ?? this.data?.viewportOffset);
    const scrollMaxHeight = this.toCssSize(this.tableConfig.scrollMaxHeight ?? this.data?.scrollMaxHeight);
    const scrollViewportOffset = this.toCssSize(
      this.tableConfig.scrollViewportOffset ?? this.data?.scrollViewportOffset,
    );
    const mobileViewportOffset = this.toCssSize(
      this.tableConfig.mobileViewportOffset ?? this.data?.mobileViewportOffset,
    );
    const mobileScrollViewportOffset = this.toCssSize(
      this.tableConfig.mobileScrollViewportOffset ?? this.data?.mobileScrollViewportOffset,
    );
    if (headerCellPadding) style['--dynamic-table-header-cell-padding'] = headerCellPadding;
    if (bodyCellPadding) style['--dynamic-table-body-cell-padding'] = bodyCellPadding;
    if (headerPadding) style['--dynamic-table-header-padding'] = headerPadding;
    if (scrollPadding) style['--dynamic-table-scroll-padding'] = scrollPadding;
    if (tableMinWidth) style['--dynamic-table-min-width'] = tableMinWidth;
    if (titleFontSize) style['--dynamic-table-title-font-size'] = titleFontSize;
    if (headerFontSize) style['--dynamic-table-header-font-size'] = headerFontSize;
    if (bodyFontSize) style['--dynamic-table-body-font-size'] = bodyFontSize;
    if (headerTextColor) style['--dynamic-table-header-text-color'] = headerTextColor;
    if (headerBackgroundColor) style['--dynamic-table-header-bg-color'] = headerBackgroundColor;
    if (rowBackgroundColor) style['--dynamic-table-row-bg-color'] = rowBackgroundColor;
    if (altRowBackgroundColor) style['--dynamic-table-alt-row-bg-color'] = altRowBackgroundColor;
    if (minHeight) style['--dynamic-table-min-height'] = minHeight;
    if (viewportOffset) style['--dynamic-table-viewport-offset'] = viewportOffset;
    if (scrollMaxHeight) style['--dynamic-table-scroll-max-height'] = scrollMaxHeight;
    if (scrollViewportOffset) style['--dynamic-table-scroll-viewport-offset'] = scrollViewportOffset;
    if (mobileViewportOffset) style['--dynamic-table-mobile-viewport-offset'] = mobileViewportOffset;
    if (mobileScrollViewportOffset) {
      style['--dynamic-table-scroll-mobile-viewport-offset'] = mobileScrollViewportOffset;
    }
    return style;
  }

  get shouldUseAutoHeight(): boolean {
    if (this.loading || this.errorMessage || this.rows.length === 0) return false;
    if (this.tableConfig.autoHeight !== true) return false;
    return this.rows.length <= this.scrollAfterRows;
  }

  private get scrollAfterRows(): number {
    const raw = Number(this.tableConfig.scrollAfterRows ?? 5);
    return Number.isFinite(raw) && raw > 0 ? raw : 5;
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadRows(filters),
      getChart: () => null,
      getSeriesNames: () => [],
      getCategories: () => this.rows.map((row) => this.getFirstDisplayValue(row)),
    });

    this.loadRows(initialFilters ?? undefined);
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.syncGridWidgetHeight());
  }

  ngOnDestroy(): void {
    if (this.heightRafId !== null) {
      cancelAnimationFrame(this.heightRafId);
    }
    this.dispatchHeightOverride(true);
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.popupRequestSub?.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  closeHoverPopupOnEscape(): void {
    this.hideHoverPopup();
  }

  loadRows(filters?: any): void {
    this.loading = true;
    this.errorMessage = '';

    const inlineData = this.data?.response || this.data?.rows || this.data?.data;
    if (inlineData) {
      this.setRowsFromResponse(inlineData);
      return;
    }

    this.configService.getConfig(this.data?.apiUrl, this.data, filters, this.data?.method)?.subscribe({
      next: (response: any) => this.setRowsFromResponse(response),
      error: () => {
        this.rows = [];
        this.loading = false;
        this.errorMessage = String(this.data?.errorMessage || 'Unable to load table data.');
      },
    });
  }

  exportCsv(): void {
    const columns = this.columns;
    if (!columns.length) return;

    const header = columns.map((column) => this.escapeCsv(column.header));
    const body = this.rows.map((row) =>
      columns.map((column) => this.escapeCsv(this.formatCellValue(row, column, false))).join(','),
    );
    const csv = [header.join(','), ...body].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.resolveExportFileName();
    link.click();
    URL.revokeObjectURL(url);
  }

  trackByColumn = (_: number, column: DynamicTableColumnConfig): string => column.field;

  trackByRow = (index: number, row: any): string => String(row?.id ?? row?.uuid ?? index);

  getCellValue(row: any, column: DynamicTableColumnConfig): any {
    return this.getValueByPath(row, column.field);
  }

  formatCellValue(row: any, column: DynamicTableColumnConfig, forDisplay = true): string {
    const value = this.getCellValue(row, column);
    if (value === null || value === undefined || value === '') {
      return column.emptyValue ?? '';
    }

    switch (column.type || 'text') {
      case 'number':
        return this.formatNumber(value, column);
      case 'currency':
        return this.formatCurrency(value, column);
      case 'percentage':
        return this.formatPercentage(value, column);
      case 'badge':
        return String(value);
      case 'text':
      default:
        return `${column.prefix || ''}${String(value)}${column.suffix || ''}`;
    }
  }

  getHeaderColumnStyle(column: DynamicTableColumnConfig): Record<string, string> {
    return this.getColumnStyle(column, this.tableConfig.defaultHeaderAlign);
  }

  getBodyColumnStyle(column: DynamicTableColumnConfig): Record<string, string> {
    return this.getColumnStyle(column, this.tableConfig.defaultCellAlign);
  }

  private getColumnStyle(
    column: DynamicTableColumnConfig,
    fallbackAlign?: 'left' | 'center' | 'right',
  ): Record<string, string> {
    const style: Record<string, string> = {};
    const width = this.toCssSize(column.width);
    const minWidth = this.toCssSize(column.minWidth);
    if (width) style['width'] = width;
    if (minWidth || width) style['min-width'] = minWidth || width;
    if (column.align || fallbackAlign) style['text-align'] = column.align || fallbackAlign || 'left';
    return style;
  }

  getCellClass(column: DynamicTableColumnConfig): string {
    return [
      `dynamic-table-cell--${column.type || 'text'}`,
      column.bold ? 'dynamic-table-cell--bold' : '',
      column.align ? `dynamic-table-cell--${column.align}` : '',
    ].filter(Boolean).join(' ');
  }

  getBadgeStyle(row: any, column: DynamicTableColumnConfig): Record<string, string> {
    const key = String(this.getCellValue(row, column) ?? '');
    const badgeStyle = column.badgeConfig?.[key] || {};
    return {
      'background-color': badgeStyle.background || '#e5e7eb',
      color: badgeStyle.color || '#374151',
      'border-color': badgeStyle.borderColor || badgeStyle.background || 'transparent',
    };
  }

  hasHoverPopup(column: DynamicTableColumnConfig): boolean {
    const popup = column.hoverPopup;
    return popup?.enabled !== false && Array.isArray(popup?.fields) && popup.fields.length > 0;
  }

  showHoverPopup(_event: Event, row: any, column: DynamicTableColumnConfig): void {
    if (!this.hasHoverPopup(column)) {
      this.activeHoverPopup = null;
      return;
    }

    const popupRows = this.getHoverPopupRows(row, column);
    if (!popupRows.length) {
      this.activeHoverPopup = null;
      return;
    }

    const title = this.resolveHoverPopupTitle(row, column);
    const subtitle = this.resolveHoverPopupSubtitle(row, column);
    this.activeHoverPopup = {
      columns: popupRows.map((item) => ({ label: item.label })),
      rows: [{ values: popupRows.map((item) => item.value) }],
      title,
      subtitle,
    };

    this.loadHoverPopupFromApi(row, column, title, subtitle);
  }

  hideHoverPopup(): void {
    this.popupRequestSub?.unsubscribe();
    this.popupRequestSub = undefined;
    this.popupRequestId += 1;
    this.activeHoverPopup = null;
  }

  trackByPopupColumn = (index: number): number => index;

  trackByPopupRow = (index: number): number => index;

  private getHoverPopupRows(row: any, column: DynamicTableColumnConfig): { label: string; value: string }[] {
    const fields = column.hoverPopup?.fields;
    if (!Array.isArray(fields)) return [];

    return fields.map((field) => ({
      label: String(field.label || ''),
      value: this.formatHoverPopupValue(this.resolveHoverPopupValue(row, field), field),
    }));
  }

  private loadHoverPopupFromApi(row: any, column: DynamicTableColumnConfig, title: string, subtitle: string): void {
    const api = column.hoverPopup?.api;
    const apiUrl = String(api?.apiUrl || this.data?.hoverPopupApiUrl || this.data?.apiUrl || '').trim();
    if (!api || api.enabled === false || !apiUrl) {
      return;
    }

    const requestData = this.buildHoverPopupRequestData(row, column);
    if (!requestData || Object.keys(requestData).length === 0) {
      return;
    }

    const requestId = ++this.popupRequestId;
    this.popupRequestSub?.unsubscribe();
    this.activeHoverPopup = {
      columns: this.getHoverPopupRows(row, column).map((item) => ({ label: item.label })),
      rows: [],
      title: api.title || title,
      subtitle: api.subtitle || subtitle,
      loading: true,
    };

    const version = String(api.version || this.data?.version || '1.0');
    const requestConfig = {
      apiUrl,
      method: api.method || this.data?.method || 'post',
      skipDefaultPayload: true,
      customPayload: {
        requestData,
        responseList: [],
        entity: api.entity || this.data?.entity || 'default_entity',
        workflowId: api.workflowId || this.data?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: api.functionName || this.data?.functionName || 'DefaultService',
            version,
          },
        ],
        communicationType: 'rest',
        version,
      },
      functionName: api.functionName || this.data?.functionName,
      version,
    };

    this.popupRequestSub = this.configService.getConfig(apiUrl, requestConfig, undefined, requestConfig.method)?.subscribe({
      next: (response: any) => {
        if (requestId !== this.popupRequestId) return;
        const responseRows = this.extractHoverPopupRows(response, column);
        const fields = column.hoverPopup?.fields || [];
        this.activeHoverPopup = {
          columns: fields.map((field) => ({ label: String(field.label || '') })),
          rows: responseRows.map((responseRow) => ({
            values: fields.map((field) => this.formatHoverPopupValue(this.resolveHoverPopupValue(responseRow, field), field)),
          })),
          title: api.title || title,
          subtitle: api.subtitle || subtitle,
          loading: false,
          errorMessage: responseRows.length ? '' : api.errorMessage || 'No details available.',
        };
      },
      error: () => {
        if (requestId !== this.popupRequestId) return;
        this.activeHoverPopup = {
          columns: this.getHoverPopupRows(row, column).map((item) => ({ label: item.label })),
          rows: [],
          title: api.title || title,
          subtitle: api.subtitle || subtitle,
          loading: false,
          errorMessage: api.errorMessage || 'Unable to load details.',
        };
      },
    });
  }

  private buildHoverPopupRequestData(row: any, column: DynamicTableColumnConfig): Record<string, any> | null {
    const api = column.hoverPopup?.api;
    if (!api) return null;

    const requestData: Record<string, any> = { ...(api.requestData || {}) };
    const fieldMap = api.requestFieldMap && Object.keys(api.requestFieldMap).length
      ? api.requestFieldMap
      : { [column.field]: column.field };
    const wrapAsArray = api.requestValuesAsArray !== false;

    Object.entries(fieldMap).forEach(([requestKey, sourceFields]) => {
      const candidates = Array.isArray(sourceFields) ? sourceFields : [sourceFields];
      const value = this.resolveFirstAvailableValue(row, candidates.filter(Boolean));
      if (value === undefined) return;
      requestData[requestKey] = wrapAsArray ? [value] : value;
    });

    return requestData;
  }

  private extractHoverPopupRows(response: any, column: DynamicTableColumnConfig): any[] {
    const api = column.hoverPopup?.api;
    const configuredPath = api?.dataPath || api?.responsePath || 'responseList[0].data';
    const rows = this.getValueByPath(response, configuredPath);
    if (Array.isArray(rows)) return rows;
    if (Array.isArray(response?.responseList?.[0]?.data)) return response.responseList[0].data;
    if (Array.isArray(response?.data)) return response.data;
    if (response && typeof response === 'object' && !this.isEnvelopeResponse(response)) return [response];
    return [];
  }

  private resolveHoverPopupTitle(row: any, column: DynamicTableColumnConfig): string {
    return `${column.header || 'Details'} Details`;
  }

  private resolveHoverPopupSubtitle(row: any, column: DynamicTableColumnConfig): string {
    return this.formatCellValue(row, column) || 'Selected record';
  }

  private resolveHoverPopupValue(row: any, fieldConfig: DynamicTableHoverPopupField): any {
    const fieldCandidates = [
      ...(Array.isArray(fieldConfig.field) ? fieldConfig.field : fieldConfig.field ? [fieldConfig.field] : []),
      ...(Array.isArray(fieldConfig.fields) ? fieldConfig.fields : []),
    ].filter(Boolean);

    const directValue = this.resolveFirstAvailableValue(row, fieldCandidates);
    if (directValue !== undefined) {
      return directValue;
    }

    if (fieldConfig.calculation === 'discountedPrice') {
      const baseCandidates = Array.isArray(fieldConfig.baseField)
        ? fieldConfig.baseField
        : fieldConfig.baseField
          ? [fieldConfig.baseField]
          : [];
      const discountCandidates = Array.isArray(fieldConfig.discountField)
        ? fieldConfig.discountField
        : fieldConfig.discountField
          ? [fieldConfig.discountField]
          : [];
      const baseValue = Number(this.resolveFirstAvailableValue(row, baseCandidates));
      const discountValue = Number(this.resolveFirstAvailableValue(row, discountCandidates));

      if (Number.isFinite(baseValue) && Number.isFinite(discountValue)) {
        const normalizedDiscount = Math.abs(discountValue) > 1 ? discountValue / 100 : discountValue;
        return baseValue * (1 - normalizedDiscount);
      }
    }

    return fieldConfig.emptyValue ?? '-';
  }

  private resolveFirstAvailableValue(row: any, fieldCandidates: string[]): any {
    for (const field of fieldCandidates) {
      const value = this.getValueByPath(row, field);
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private formatHoverPopupValue(value: any, fieldConfig: DynamicTableHoverPopupField): string {
    if (value === null || value === undefined || value === '') {
      return fieldConfig.emptyValue ?? '-';
    }

    const format = String(fieldConfig.format || '').toLowerCase();
    const column: DynamicTableColumnConfig = {
      field: '',
      header: fieldConfig.label,
      type: fieldConfig.type || 'text',
      decimals: fieldConfig.decimals,
      currency: fieldConfig.currency,
      compact: fieldConfig.compact,
      valueMultiplier: fieldConfig.valueMultiplier,
      prefix: fieldConfig.prefix,
      suffix: fieldConfig.suffix,
      emptyValue: fieldConfig.emptyValue,
    };

    if (format === 'currencycompact' || format === 'compactcurrency') {
      column.type = 'currency';
      column.compact = true;
      column.decimals = fieldConfig.decimals ?? 0;
    } else if (format === 'currency') {
      column.type = 'currency';
    } else if (format === 'percentage') {
      column.type = 'percentage';
    } else if (format === 'number') {
      column.type = 'number';
    }

    switch (column.type || 'text') {
      case 'number':
        return this.formatNumber(value, column);
      case 'currency':
        return this.formatCurrency(value, column);
      case 'percentage':
        return this.formatPercentage(value, column);
      case 'badge':
      case 'text':
      default:
        return `${column.prefix || ''}${String(value)}${column.suffix || ''}`;
    }
  }

  private setRowsFromResponse(response: any): void {
    const extracted = this.extractRows(response);
    this.rows = this.sortRows(extracted);
    this.loading = false;
    this.wrapper.setSourceData(this.rows, this.columns[0]?.field || 'id');
    this.syncGridWidgetHeight();
  }

  private sortRows(rows: any[]): any[] {
    const sortConfig = this.tableConfig.sort || this.data?.sort || {};
    const firstColumnField = this.columns[0]?.field || '';
    const fields = (Array.isArray(sortConfig.fields) ? sortConfig.fields : [sortConfig.field])
      .map((field: any) => String(field || '').trim())
      .filter(Boolean);
    if (!fields.length && sortConfig.byFirstColumn === true && firstColumnField) {
      fields.push(firstColumnField);
    }
    if (!fields.length) {
      return rows;
    }

    const direction = String(sortConfig.direction || 'asc').toLowerCase() === 'desc' ? -1 : 1;
    const type = String(sortConfig.type || 'natural').toLowerCase();
    const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });

    return [...rows].sort((left, right) => {
      for (const field of fields) {
        const leftValue = this.getValueByPath(left, field);
        const rightValue = this.getValueByPath(right, field);
        const result = type === 'number'
          ? ((Number(leftValue) || 0) - (Number(rightValue) || 0))
          : collator.compare(String(leftValue ?? ''), String(rightValue ?? ''));

        if (result !== 0) {
          return result * direction;
        }
      }

      return 0;
    });
  }

  private extractRows(response: any): any[] {
    if (Array.isArray(response)) return response;

    const configuredPath = this.tableConfig.dataPath || this.data?.dataPath || this.data?.rowsPath || 'responseList[0].data';
    const rows = this.getValueByPath(response, configuredPath);
    if (Array.isArray(rows)) return rows;

    if (Array.isArray(response?.responseList?.[0]?.data)) return response.responseList[0].data;
    if (Array.isArray(response?.data)) return response.data;
    if (response && typeof response === 'object' && !this.isEnvelopeResponse(response)) return [response];
    return [];
  }

  private isEnvelopeResponse(response: any): boolean {
    return !!(
      response?.responseList ||
      response?.requestData ||
      response?.endpoints ||
      response?.workflowId ||
      response?.entity ||
      response?.commitStatus
    );
  }

  private getValueByPath(source: any, path?: string): any {
    if (!path) return source;
    const normalized = String(path).replace(/\[(\d+)\]/g, '.$1');
    return normalized.split('.').filter(Boolean).reduce((current, key) => current?.[key], source);
  }

  private formatNumber(value: any, column: DynamicTableColumnConfig): string {
    const num = this.toDisplayNumber(value, column);
    if (!Number.isFinite(num)) return String(value);
    const decimals = Number.isFinite(Number(column.decimals)) ? Number(column.decimals) : 0;
    return `${column.prefix || ''}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num)}${column.suffix || ''}`;
  }

  private formatCurrency(value: any, column: DynamicTableColumnConfig): string {
    const num = this.toDisplayNumber(value, column);
    if (!Number.isFinite(num)) return String(value);
    if (column.compact === true) {
      const abs = Math.abs(num);
      const divisor = abs >= 1000000 ? 1000000 : abs >= 1000 ? 1000 : 1;
      const suffix = divisor === 1000000 ? 'M' : divisor === 1000 ? 'k' : '';
      const decimals = Number.isFinite(Number(column.decimals)) ? Number(column.decimals) : 0;
      return `$${(num / divisor).toFixed(decimals)}${suffix}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: column.currency || 'USD',
      maximumFractionDigits: Number.isFinite(Number(column.decimals)) ? Number(column.decimals) : 0,
    }).format(num);
  }

  private formatPercentage(value: any, column: DynamicTableColumnConfig): string {
    const num = this.toDisplayNumber(value, column);
    if (!Number.isFinite(num)) return String(value);
    const decimals = Number.isFinite(Number(column.decimals)) ? Number(column.decimals) : 0;
    return `${num.toFixed(decimals)}%`;
  }

  private toDisplayNumber(value: any, column: DynamicTableColumnConfig): number {
    const num = Number(value);
    if (!Number.isFinite(num)) return num;
    const multiplier = Number(column.valueMultiplier ?? 1);
    return Number.isFinite(multiplier) ? num * multiplier : num;
  }

  private escapeCsv(value: any): string {
    const text = String(value ?? '');
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  private resolveExportFileName(): string {
    const configured = this.tableConfig.exportFileName || this.data?.exportFileName || this.data?.widgetId || 'dynamic-table';
    return String(configured).toLowerCase().endsWith('.csv') ? String(configured) : `${configured}.csv`;
  }

  private getFirstDisplayValue(row: any): string {
    const firstColumn = this.columns[0];
    return firstColumn ? String(this.getCellValue(row, firstColumn) ?? '') : '';
  }

  private toCssSize(value: string | number | undefined): string {
    if (value === undefined || value === null || value === '') return '';
    return typeof value === 'number' ? `${value}px` : String(value);
  }

  private syncGridWidgetHeight(): void {
    if (this.heightRafId !== null) {
      cancelAnimationFrame(this.heightRafId);
    }

    this.heightRafId = requestAnimationFrame(() => {
      this.heightRafId = null;
      this.dispatchHeightOverride(!this.shouldUseAutoHeight);
    });
  }

  private dispatchHeightOverride(restore: boolean): void {
    if (typeof window === 'undefined' || !this.data?.widgetId) return;

    const detail = restore
      ? { widgetId: this.data.widgetId, restore: true }
      : { widgetId: this.data.widgetId, restore: false, heightPx: this.measureAutoHeight() };

    window.dispatchEvent(new CustomEvent('dvm-widget-height-override', { detail }));
  }

  private measureAutoHeight(): number {
    const measuredHeight = this.cardRoot?.nativeElement?.scrollHeight || 0;
    if (measuredHeight > 0) {
      return Math.ceil(measuredHeight);
    }

    const headerCellPadding = this.parseVerticalPadding(this.tableConfig.headerCellPadding, 24);
    const bodyCellPadding = this.parseVerticalPadding(this.tableConfig.bodyCellPadding, 24);
    const headerHeight = 22 + headerCellPadding;
    const rowHeight = 26 + bodyCellPadding;
    const titleHeight = this.title ? 56 : 16;
    const outerPadding = 30;
    return Math.ceil(titleHeight + outerPadding + headerHeight + (this.rows.length * rowHeight));
  }

  private parseVerticalPadding(padding: string | undefined, fallback: number): number {
    const value = String(padding || '').trim();
    if (!value) return fallback;
    const parts = value.split(/\s+/).map((part) => parseFloat(part)).filter((part) => Number.isFinite(part));
    if (!parts.length) return fallback;
    if (parts.length === 1) return parts[0] * 2;
    return parts[0] + parts[2 % parts.length];
  }
}
